import {
  isLtw365Tenant,
  ltw365Data,
} from '../../../../../helpers/ltw365TenantHelpers';
import { TenantDomainId } from '../../../../../store/features/pageConfig';
import { isKnect365Tenant } from '../../../../../helpers/knect365TenantHelpers';
import { DivisionalMarketingFooterConfig } from './types';

function makeAsiaTechConfig(): DivisionalMarketingFooterConfig {
  return {
    discoverTitle: 'Discover more from Informa Tech',
    menuRight: [
      {
        title: 'Sustainability',
        href: '/home/sustainability/',
      },
      {
        title: 'TMT Events',
        href: 'https://tmt.knect365.com/',
      },
      {
        title: 'Tourism',
        href: 'https://get.connectechasia.com/tourism/',
      },
    ],
    menuLeft: [
      {
        title: 'Advisory Committee',
        href: '/home/advisory-committee/',
      },
      {
        title: 'Asia Tech x Singapore',
        href: 'https://asiatechxsg.com/',
      },
      {
        title: 'Contact Us',
        href: '/home/contact/',
      },
    ],
    divisionalFooter: [
      {
        title: 'Home',
        href: 'https://tech.informa.com/',
      },
      {
        title: 'Cookies',
        href: 'https://knect365.com/cookie-policy/',
      },
      {
        title: 'CCPA: Do not sell my personal info',
        href: 'https://privacyportal-eu-cdn.onetrust.com/dsarwebform/c1f53e84-9f05-4169-a854-85052b63c50b/5f26b553-52cc-4973-a761-295d5634a6b6.html',
      },
      {
        title: 'Privacy',
        href: 'https://www.informa.com/privacy-policy/',
      },
      {
        title: 'Terms',
        href: 'https://tech.informa.com/terms-and-conditions/',
      },
    ],
    secondMenuTitle: '',
    secondaryMenu: [],
    lowerFooter: {
      logo: {
        link: 'https://tech.informa.com/',
        target: '_self',
        image: '/images/informa-tech-logo-footer@2x.png',
        alt: 'Informa Tech logo',
        prefix: 'Brought to you by',
        logoType: 'techlogo',
      },
    },
    copyright: [
      'Copyright © 2024 Informa PLC. Informa Telecoms & Media Limited is a company registered in England and Wales with company number 00991704 whose registered office is 5 Howick Place, London, SW1P 1WG. VAT GB365462636. Informa Telecoms & Media Limited is part of Informa PLC',
    ],
  };
}

function makeKnect365Config(): DivisionalMarketingFooterConfig {
  return {
    discoverTitle: 'Discover more from Informa Tech',
    menuLeft: [
      {
        title: 'Informa Tech Home',
        href: 'https://tech.informa.com/',
      },
      {
        title: 'Event Calendar',
        href: 'https://tech.informa.com/events',
      },
      {
        title: 'TMT Events',
        href: 'https://tmt.knect365.com',
      },
      {
        title: 'Automotive Events',
        href: 'https://automotive.knect365.com/',
      },
    ],
    menuRight: [
      {
        title: 'Our Brands',
        href: 'https://tech.informa.com/brands',
      },
      {
        title: 'London Tech Week',
        href: 'https://londontechweek.com/',
      },
      {
        title: 'Asia Tech x Singapore',
        href: 'https://asiatechxsg.com/',
      },
    ],
    secondMenuTitle: 'Working with us',
    secondaryMenu: [
      {
        title: 'About Us',
        href: 'https://tech.informa.com/about-us/',
      },
      {
        title: 'Contact Us',
        href: '/contact/',
        withSitePath: true,
      },
    ],
    divisionalFooter: [
      {
        title: 'Home',
        href: 'https://tech.informa.com/',
      },
      {
        title: 'Cookies',
        href: 'https://knect365.com/cookie-policy/',
      },
      {
        title: 'CCPA: Do not sell my personal info',
        href: 'https://privacyportal-eu-cdn.onetrust.com/dsarwebform/c1f53e84-9f05-4169-a854-85052b63c50b/5f26b553-52cc-4973-a761-295d5634a6b6.html',
      },
      {
        title: 'Privacy',
        href: 'https://www.informa.com/privacy-policy/',
      },
      {
        title: 'Terms',
        href: 'https://tech.informa.com/terms-and-conditions/',
      },
    ],
    lowerFooter: {
      logo: {
        link: 'https://tech.informa.com/',
        target: '_self',
        image: '/images/informa-tech-logo-footer@2x.png',
        alt: 'Informa Tech logo',
        prefix: 'Brought to you by',
        logoType: 'techlogo',
      },
    },
  };
}

function makeDefaultDivisionalMarketingConfig(data?: {
  terms?: string;
}): DivisionalMarketingFooterConfig {
  const termsUrl =
    data?.terms ?? 'https://tech.informa.com/terms-and-conditions/';

  return {
    discoverTitle: 'Discover more from Informa',
    menuLeft: [
      {
        title: 'Informa Home',
        href: 'https://www.informa.com/',
      },
      {
        title: 'London Tech Week',
        href: 'https://londontechweek.com/',
      },
      {
        title: 'Asia Tech x Singapore',
        href: 'https://asiatechxsg.com/',
      },
    ],
    menuRight: [],
    secondMenuTitle: 'Working with us',
    secondaryMenu: [
      {
        title: 'About Us',
        href: 'https://www.informa.com/about-us/',
      },
      {
        title: 'Contact Us',
        href: '/contact/',
        withSitePath: true,
      },
    ],
    lowerFooter: {
      logo: {
        link: 'https://www.informa.com/',
        target: '_self',
        image: '/images/informa-global.png',
        alt: 'Informa',
        prefix: 'Brought to you by',
        logoType: 'techlogo',
      },
    },
    divisionalFooter: [
      {
        title: 'Home',
        href: 'https://www.informa.com/',
      },
      {
        title: 'Cookies',
        href: 'https://knect365.com/cookie-policy/',
      },
      {
        title: 'CCPA: Do not sell my personal info',
        href: 'https://privacyportal-eu-cdn.onetrust.com/dsarwebform/c1f53e84-9f05-4169-a854-85052b63c50b/5f26b553-52cc-4973-a761-295d5634a6b6.html',
      },
      {
        title: 'Privacy',
        href: 'https://www.informa.com/privacy-policy/',
      },
      {
        title: 'Terms',
        href: termsUrl,
      },
    ],
  };
}

function makeLtw365Config(): DivisionalMarketingFooterConfig {
  return makeDefaultDivisionalMarketingConfig(ltw365Data);
}

export function getDivisionalMarketingFooterConfig(
  tenantId: TenantDomainId | null | undefined,
): DivisionalMarketingFooterConfig {
  if (tenantId === TenantDomainId.ASIATECHXSINGAPORE) {
    return makeAsiaTechConfig();
  }

  // Guard undefined/null tenantId before calling helpers expecting TenantDomainId
  if (tenantId && isKnect365Tenant(tenantId)) {
    return makeKnect365Config();
  }

  if (tenantId && isLtw365Tenant(tenantId)) {
    return makeLtw365Config();
  }

  return makeDefaultDivisionalMarketingConfig();
}
